/**
 * enhancedDataService — SINGLE canonical data service for all global data.
 *
 * This is the only data service in the frontend.
 * src/services/dataService.js was removed (dead code, zero imports).
 *
 * Webinar data: reads exclusively from the `webinars` table.
 * Legacy tables `events` and `upcoming_events` are NOT queried here.
 */
import { supabase } from '../lib/supabase';
import { readSessionToken, readVerifiedEmail } from '../contexts/AuthContext';

let eventQueue = [];
let flushTimer = null;

const flushAnalyticsQueue = async () => {
  if (eventQueue.length === 0) return;
  const currentBatch = [...eventQueue];
  eventQueue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  try {
    await fetch('/api/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(currentBatch)
    });
  } catch (err) {
    console.warn('[enhancedDataService] analytics flush failed:', err.message);
  }
};

const enhancedDataService = {
  initialize: async () => {},

  forceRefresh: async () => {
    const [website, posts, categories, events, submissions, profiles] = await Promise.allSettled([
      enhancedDataService.getWebsiteContent(),
      enhancedDataService.getForumPosts(),
      enhancedDataService.getForumCategories(),
      enhancedDataService.getUpcomingEvents(),
      enhancedDataService.getContestSubmissions(),
      enhancedDataService.getUserProfiles(),
    ]);
    return {
      website:     website.status     === 'fulfilled' ? website.value     : {},
      posts:       posts.status       === 'fulfilled' ? posts.value       : [],
      categories:  categories.status  === 'fulfilled' ? categories.value  : [],
      events:      events.status      === 'fulfilled' ? events.value      : [],
      submissions: submissions.status === 'fulfilled' ? submissions.value : [],
      profiles:    profiles.status    === 'fulfilled' ? profiles.value    : [],
    };
  },

  getSavedWebinars: async (userEmail) => {
    if (!supabase || !userEmail) return [];
    try {
      const { data, error } = await supabase
        .from('saved_webinars')
        .select('webinar_id')
        .eq('user_email', userEmail);
      if (error) {
        console.warn('[enhancedDataService] fetch saved webinars error:', error.message);
        return [];
      }
      return (data || []).map(row => row.webinar_id);
    } catch (e) {
      console.warn('[enhancedDataService] fetch saved webinars failed:', e.message);
      return [];
    }
  },

  saveWebinar: async (userEmail, webinarId) => {
    if (!supabase || !userEmail || !webinarId) return false;
    try {
      const { error } = await supabase
        .from('saved_webinars')
        .insert({ user_email: userEmail, webinar_id: webinarId });
      if (error) {
        console.warn('[enhancedDataService] save webinar error:', error.message);
        return false;
      }
      return true;
    } catch (e) {
      console.warn('[enhancedDataService] save webinar failed:', e.message);
      return false;
    }
  },

  unsaveWebinar: async (userEmail, webinarId) => {
    if (!supabase || !userEmail || !webinarId) return false;
    try {
      const { error } = await supabase
        .from('saved_webinars')
        .delete()
        .eq('user_email', userEmail)
        .eq('webinar_id', webinarId);
      if (error) {
        console.warn('[enhancedDataService] unsave webinar error:', error.message);
        return false;
      }
      return true;
    } catch (e) {
      console.warn('[enhancedDataService] unsave webinar failed:', e.message);
      return false;
    }
  },

  getWebsiteContent: async () => {
    if (!supabase) return {};
    try {
      const { data, error } = await supabase
        .from('website_content')
        .select('content')
        .eq('id', 1)
        .single();
      if (error) return {};
      return data?.content || {};
    } catch { return {}; }
  },

  getForumPosts: async () => {
    // Prefer server API (service role) — matches create path and avoids broken embeds
    try {
      const token = readSessionToken();
      const email = readVerifiedEmail();
      let url = '/api/community?action=posts&limit=100';
      if (token && email) {
        url += `&author_email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
      }
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        const json = await res.json();
        if (Array.isArray(json.posts)) return json.posts;
      }
    } catch (e) {
      console.warn('[getForumPosts] API fetch failed:', e.message);
    }

    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from('forum_posts')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) {
        console.warn('[getForumPosts] Supabase error:', error.message);
        return [];
      }
      return data || [];
    } catch (e) {
      console.warn('[getForumPosts] failed:', e.message);
      return [];
    }
  },

  getForumCategories: async () => {
    // Prefer server API (service role) — bypasses RLS and handles schema differences
    try {
      const res = await fetch('/api/community?action=categories', { cache: 'no-store' });
      if (res.ok) {
        const json = await res.json();
        if (Array.isArray(json.categories) && json.categories.length > 0) return json.categories;
      }
    } catch (e) {
      console.warn('[getForumCategories] API fetch failed:', e.message);
    }

    // Fallback: direct Supabase query
    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from('forum_categories')
        .select('*')
        .order('name');
      if (error) return [];
      return (data || []).filter(c => c.is_active !== false);
    } catch { return []; }
  },

  // Fetch from 'webinars' table exclusively — single source of truth
  getUpcomingEvents: async () => {
    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from('webinars')
        .select('id, title, event_time, speaker, speaker_title, image_url, is_featured, is_pinned, registrations_count, description, timezone, external_event_url, pricing_type, category, tags, source_reliability_score, status')
        .eq('status', 'published')
        .order('is_pinned',   { ascending: false })   // pinned first
        .order('is_featured', { ascending: false })   // featured second
        .order('event_time',  { ascending: true });   // soonest third

      if (error) {
        console.warn('[enhancedDataService] webinars fetch error:', error.message);
        return [];
      }
      
      const sanitized = (data || []).map(event => {
        let url = event.external_event_url;
        let isValid = true;
        if (!url) {
          isValid = false;
        } else {
          try {
            const parsed = new URL(url);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
              isValid = false;
            } else if (
              url.includes('sessionize.com/playwright-summit') ||
              url.includes('sessionize.com/ai-quality-2024') ||
              url.includes('dev.to/events/devops-2024')
            ) {
              isValid = false;
            }
          } catch {
            isValid = false;
          }
        }
        return {
          ...event,
          external_event_url: isValid ? url : null
        };
      });

      return sanitized;
    } catch (e) {
      console.warn('[enhancedDataService] webinars fetch failed:', e.message);
      return [];
    }
  },

  getSavedWebinarsDetails: async (savedIds = []) => {
    if (!supabase || !Array.isArray(savedIds) || savedIds.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('webinars')
        .select('id, title, event_time, speaker, speaker_title, image_url, is_featured, is_pinned, registrations_count, description, timezone, external_event_url, pricing_type, category, tags, source_reliability_score, status')
        .in('id', savedIds)
        .in('status', ['published', 'archived'])
        .order('event_time', { ascending: true });

      if (error) {
        console.warn('[enhancedDataService] getSavedWebinarsDetails error:', error.message);
        return [];
      }

      const sanitized = (data || []).map(event => {
        let url = event.external_event_url;
        let isValid = true;
        if (!url) {
          isValid = false;
        } else {
          try {
            const parsed = new URL(url);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
              isValid = false;
            }
          } catch {
            isValid = false;
          }
        }
        return {
          ...event,
          external_event_url: isValid ? url : null
        };
      });

      return sanitized;
    } catch (e) {
      console.warn('[enhancedDataService] getSavedWebinarsDetails failed:', e.message);
      return [];
    }
  },

  getContestSubmissions: async () => {
    if (!supabase) return [];
    try {
      const { data, error } = await supabase
        .from('contest_submissions')
        .select('*')
        .order('winner_announcement_timestamp', { ascending: false, nullsFirst: false })
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) return [];
      return (data || []).filter(s => {
        const name = (s.name || s.contributorName || s.participant_name || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        const title = (s.submissionTitle || s.technique_title || s.title || '').toLowerCase();
        const isDummy = name.includes('e2e') || name.includes('test user') || name.includes('test admin') ||
                        email.includes('e2e') || email.includes('mailinator') || email.includes('example.com') ||
                        title.includes('e2e test') || title.includes('local test');
        return !isDummy;
      });
    } catch { return []; }
  },

  getUserProfiles: async () => {
    if (!supabase) return [];
    try {
      // Use select('*') to dynamically fetch all available columns.
      // This ensures compatibility across environments where columns like avatar_url might be missing,
      // avoiding HTTP 400 (Bad Request) errors.
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .limit(200);
      if (error) {
        console.warn('[enhancedDataService] getUserProfiles error:', error.message);
        return [];
      }
      return data || [];
    } catch (e) {
      console.warn('[enhancedDataService] getUserProfiles failed:', e.message);
      return [];
    }
  },

  trackEvent: async (eventName, metadata = {}) => {
    try {
      let email = null;
      try {
        const raw = localStorage.getItem('auth_session');
        if (raw) {
          email = JSON.parse(raw)?.email;
        }
      } catch (_) {}

      eventQueue.push({
        event_name: eventName,
        event_metadata: metadata,
        user_email: email
      });

      if (eventQueue.length >= 10) {
        flushAnalyticsQueue();
      } else if (!flushTimer) {
        flushTimer = setTimeout(flushAnalyticsQueue, 5000);
      }
    } catch (_) {}
  },

};

export default enhancedDataService;
